/**
 * Every finished workout, written to Health Connect once (`D281`), when the
 * person switched it on in the profile.
 *
 * A controller fed with every new session, like the rest alert: it acts on
 * the session ENDING (complete or early — an abandoned workout is not a
 * session anyone did, `D177`, and never reaches `ended` here). The native
 * record is keyed on the workout's id, so a second write updates rather than
 * duplicates; the stored "last written" id is only there to spare the
 * bridge call on every later change of the same ended session.
 *
 * Pure apart from its injected ports; `startHealthExport` (`health-row.tsx`)
 * wires the real ones.
 */
import type { KeyValueStorage } from "@repo/client/i18n";
import type { HealthWorkout } from "../../native";
import type { WorkoutSession } from "../workout/store/session-model";

/** The switch, per device: `"on"` or absent. */
export const HEALTH_EXPORT_KEY = "mypocketgym.health-connect.v1";
/** The id of the last workout written, per device. */
export const HEALTH_LAST_KEY = "mypocketgym.health-connect.last.v1";

export interface HealthExportDeps {
  readonly storage: Pick<KeyValueStorage, "getItem" | "setItem">;
  /** The workout's name as the person reads it, or absent. */
  readonly titleOf: (session: WorkoutSession) => string | undefined;
  readonly write: (workout: HealthWorkout) => Promise<boolean>;
}

/** The record a session becomes, or `null` while it is not a finished one. */
export function healthWorkoutOf(
  session: WorkoutSession | null,
  title?: string
): HealthWorkout | null {
  if (
    session === null ||
    session.ended === null ||
    session.endedAt === null ||
    session.endedAt <= session.startedAt
  ) {
    return null;
  }
  return {
    endedAt: session.endedAt,
    id: session.sessionId,
    startedAt: session.startedAt,
    ...(title === undefined ? {} : { title }),
  };
}

export function createHealthExport(deps: HealthExportDeps) {
  /** Ids already handled in this process, written or not. */
  const seen = new Set<string>();
  return {
    async update(session: WorkoutSession | null): Promise<void> {
      if (session === null || seen.has(session.sessionId)) {
        return;
      }
      const workout = healthWorkoutOf(session, deps.titleOf(session));
      if (workout === null) {
        return;
      }
      seen.add(workout.id);
      const [enabled, last] = await Promise.all([
        deps.storage.getItem(HEALTH_EXPORT_KEY),
        deps.storage.getItem(HEALTH_LAST_KEY),
      ]);
      if (enabled !== "on" || last === workout.id) {
        return;
      }
      if (await deps.write(workout)) {
        await deps.storage.setItem(HEALTH_LAST_KEY, workout.id);
      }
    },
  };
}
